import type {
  FreshStrawDiscardRequest,
  FreshStrawFreezeRequest,
  FreshStrawImplantRequest,
  FreshStrawSaveResponse,
} from '@/app/services/freshStrawRest';

import {
  buildDiscardPayload,
  buildFreezePayload,
  buildImplantPayload,
  FreshStrawDiscardSharedInputs,
  FreshStrawFreezeSharedInputs,
  FreshStrawImplantSharedInputs,
  FreshStrawRow,
  validateDiscardInputs,
  validateFreezeInputs,
  validateImplantInputs,
} from './payload';

/**
 * Pure async orchestration for batch fresh-straw saves. Factored out of the
 * React hook so it can be unit-tested with plain mocked mutation functions.
 *
 * Semantics:
 *   1. Validate each row up front. Collect ALL errors first so the user sees
 *      every problem in one pass, not one per retry.
 *   2. If validation fails, return { ok: false, errors } without calling any
 *      mutation.
 *   3. Otherwise fire the mutation for each row sequentially — parallelism is
 *      tempting but the stored procs don't handle concurrent writes against
 *      the same FertId cleanly. Sequential also makes rollback reasoning
 *      simpler (the first failing row aborts the batch).
 */

export type BatchResult<TRequest> =
  | {
      ok: true;
      rowsSaved: number;
      responses: Array<{ request: TRequest; response: FreshStrawSaveResponse }>;
    }
  | {
      ok: false;
      errors: string[];
      rowsSaved: number;
    };

type Mutation<TRequest> = (body: TRequest) => Promise<FreshStrawSaveResponse>;

async function runBatch<TRequest>(
  payloads: TRequest[],
  mutate: Mutation<TRequest>,
): Promise<BatchResult<TRequest>> {
  const responses: Array<{
    request: TRequest;
    response: FreshStrawSaveResponse;
  }> = [];

  for (const payload of payloads) {
    try {
      const response = await mutate(payload);
      responses.push({ request: payload, response });
    } catch (err) {
      return {
        ok: false,
        errors: [err instanceof Error ? err.message : String(err)],
        rowsSaved: responses.length,
      };
    }
  }

  return { ok: true, rowsSaved: responses.length, responses };
}

export async function runImplantBatch(
  rows: FreshStrawRow[],
  shared: FreshStrawImplantSharedInputs,
  mutate: Mutation<FreshStrawImplantRequest>,
): Promise<BatchResult<FreshStrawImplantRequest>> {
  const validationErrors = rows.flatMap((row, idx) =>
    validateImplantInputs(row, shared).map((msg) => `Row ${idx + 1}: ${msg}`),
  );
  if (rows.length === 0) {
    return { ok: false, errors: ['No fertilizations selected'], rowsSaved: 0 };
  }
  if (validationErrors.length > 0) {
    return { ok: false, errors: validationErrors, rowsSaved: 0 };
  }

  const payloads = rows.map((row) => buildImplantPayload(row, shared));
  return runBatch(payloads, mutate);
}

export async function runFreezeBatch(
  rows: FreshStrawRow[],
  shared: FreshStrawFreezeSharedInputs,
  mutate: Mutation<FreshStrawFreezeRequest>,
): Promise<BatchResult<FreshStrawFreezeRequest>> {
  if (rows.length === 0) {
    return { ok: false, errors: ['No fertilizations selected'], rowsSaved: 0 };
  }
  const validationErrors = rows.flatMap((row, idx) =>
    validateFreezeInputs(row, shared).map((msg) => `Row ${idx + 1}: ${msg}`),
  );
  if (validationErrors.length > 0) {
    return { ok: false, errors: validationErrors, rowsSaved: 0 };
  }

  const payloads = rows.map((row) => buildFreezePayload(row, shared));
  return runBatch(payloads, mutate);
}

export async function runDiscardBatch(
  rows: FreshStrawRow[],
  shared: FreshStrawDiscardSharedInputs,
  mutate: Mutation<FreshStrawDiscardRequest>,
): Promise<BatchResult<FreshStrawDiscardRequest>> {
  if (rows.length === 0) {
    return { ok: false, errors: ['No fertilizations selected'], rowsSaved: 0 };
  }
  const validationErrors = rows.flatMap((row, idx) =>
    validateDiscardInputs(row, shared).map((msg) => `Row ${idx + 1}: ${msg}`),
  );
  if (validationErrors.length > 0) {
    return { ok: false, errors: validationErrors, rowsSaved: 0 };
  }

  const payloads = rows.map((row) => buildDiscardPayload(row, shared));
  return runBatch(payloads, mutate);
}
